import React from "react";
import { Box, List, ListItemButton, Typography, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "@mui/icons-material";
import { formatCamelCase } from "../../../../shared/utils/formatter";
import {
  State,
  Action,
  KPIOption,
  ConfigurationPreset,
  ActionType,
} from "../../../../shared/interfaces/kpi";

interface KpiListPanelProps {
  state: State;
  dispatch: React.Dispatch<Action>;
  ALL_KPI_OPTIONS: Record<string, KPIOption>;
  selectedPreset: ConfigurationPreset;
}

const KpiListPanel: React.FC<KpiListPanelProps> = ({
  state,
  dispatch,
  ALL_KPI_OPTIONS,
  selectedPreset,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 300 },
        borderRight: 1,
        borderColor: "divider",
        p: 1,
        overflowY: "auto",
      }}
    >
      <Typography variant="overline" sx={{ pl: 1, color: "text.secondary" }}>
        AVAILABLE KPIS
      </Typography>
      <List dense>
        {Object.entries(ALL_KPI_OPTIONS)
          .filter(([_, k]) => k.isActive)
          .map(([kpi]) => {
            const savedCfg = selectedPreset.kpiSettings[kpi] || {};
            const isKeyPresentInPreset = kpi in selectedPreset.kpiSettings;
            const isCurrentlySelected = state.editingKPIKey === kpi;

            return (
              <ListItemButton
                key={kpi}
                onClick={() =>
                  dispatch({
                    type: ActionType.SELECT_KPI,
                    key: kpi,
                    config: savedCfg,
                  })
                }
                sx={{
                  mb: { xs: 1, md: 0.3 },
                  bgcolor: {
                    xs: isCurrentlySelected ? "#60A5FA" : "#FFF",
                    md: isCurrentlySelected
                      ? theme.palette.primary.light + "33"
                      : "transparent",
                  },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  border: {
                    xs: isCurrentlySelected
                      ? "1.6px solid #60A5FA"
                      : "0.8px solid #E5E7EB",
                    md: "none",
                  },
                  borderRadius: {
                    xs: isCurrentlySelected ? "8px" : "8px",
                    md: "8px",
                  },
                  p: {
                    xs: "13.6px",
                    md: "8px",
                  },
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0, py: 0.5, mr: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={isCurrentlySelected ? "bold" : "normal"}
                  >
                    {formatCamelCase(kpi)}
                  </Typography>
                </Box>

                {isKeyPresentInPreset ? (
                  <Chip
                    label="Set"
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      height: 20,
                      "& .MuiChip-label": { px: 0.5, fontSize: "0.7rem" },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 20,
                      color: "transparent",
                      minWidth: "40px",
                      overflow: "hidden",
                    }}
                  >
                    &nbsp;
                  </Box>
                )}
              </ListItemButton>
            );
          })}
      </List>
    </Box>
  );
};

export default KpiListPanel;
